import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, MapPin, Plus, Trash2, Edit, Download, AlertTriangle, History, FileText, FileSpreadsheet, Globe, Filter, SortAsc, Heart } from 'lucide-react';
import { MemberSearchInput } from '../components/MemberSearchInput';

export default function MohallaSectorManagement() {
  const { api, user, canExportExcel, isSuperAdmin } = useAuth();
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mohallas');
  const [allMembers, setAllMembers] = useState([]);
  
  // Modal states
  const [showAddMohalla, setShowAddMohalla] = useState(false);
  const [showAddSector, setShowAddSector] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [newMohalla, setNewMohalla] = useState({ 
    name: '', 
    address: '', 
    masool_name: '',
    masool_mobile: '',
    masool_its: '',
    masoola_name: '', 
    masoola_mobile: '',
    masoola_its: '',
    fmb_incharge_name: '',
    fmb_incharge_mobile: ''
  });
  const [newSector, setNewSector] = useState({ 
    name: '', 
    mohalla: '', 
    sub_sector_name: '',
    sector_name: '',
    musaid_name: '', 
    musaid_mobile: '',
    musaid_its: '',
    musaida_name: '', 
    musaida_mobile: '',
    musaida_its: ''
  });
  
  // Download states
  const [downloadScope, setDownloadScope] = useState('all');
  const [downloadMohalla, setDownloadMohalla] = useState('');
  const [downloadSector, setDownloadSector] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('pdf'); // Default to PDF
  const [listType, setListType] = useState('standard'); // 'standard' or 'shaadi'
  const [customHeading, setCustomHeading] = useState('');
  const [sectorSubheading, setSectorSubheading] = useState('');
  const [isFmb, setIsFmb] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filteredSectors, setFilteredSectors] = useState([]);
  const [sectorAreaFilter, setSectorAreaFilter] = useState('');
  const [includeMemberCount, setIncludeMemberCount] = useState(false); // Super Admin only

  const areaOptions = [...new Set(sectors.map((s) => s.area).filter(Boolean))].sort();
  const displaySectors = sectorAreaFilter ? sectors.filter((s) => s.area === sectorAreaFilter) : sectors;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [mohallasRes, sectorsRes] = await Promise.all([
        api.get('/mohallas'),
        api.get('/sectors/list-with-stats')  // Use endpoint with HOF counts
      ]);
      setMohallas(mohallasRes.data?.mohallas || []);
      setSectors(sectorsRes.data?.sectors || []);
      
      // Fetch members for search
      fetchAllMembers();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api]);
  
  const fetchAllMembers = async () => {
    try {
      let allFetched = [];
      for (let page = 1; page <= 20; page++) {
        const response = await api.get(`/members?page=${page}&limit=100`);
        const fetchedMembers = response.data.members || [];
        allFetched = [...allFetched, ...fetchedMembers];
        if (fetchedMembers.length < 100) break;
      }
      setAllMembers(allFetched);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await api.get('/audit');
      const allLogs = response.data?.logs || response.data || [];
      const filteredLogs = allLogs.filter(log => 
        log.entity_type?.includes('MOHALLA') || 
        log.entity_type?.includes('SECTOR') ||
        log.change_type === 'CREATE' ||
        log.change_type === 'DELETE'
      );
      setAuditLogs(filteredLogs.slice(0, 100));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    fetchAuditLogs();
  }, [fetchData, fetchAuditLogs]);

  // Filter sectors when mohalla changes in download
  useEffect(() => {
    if (downloadMohalla) {
      setFilteredSectors(sectors.filter(s => s.mohalla === downloadMohalla));
    } else {
      setFilteredSectors([]);
    }
    setDownloadSector('');
  }, [downloadMohalla, sectors]);

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // === MOHALLA CRUD ===
  const handleAddMohalla = async () => {
    if (!newMohalla.name.trim()) {
      toast.error('Mohalla name is required');
      return;
    }
    try {
      await api.post('/mohallas', newMohalla);
      toast.success('Mohalla added successfully');
      setShowAddMohalla(false);
      setNewMohalla({ 
        name: '', 
        address: '', 
        masool_name: '',
        masool_mobile: '',
        masoola_name: '', 
        masoola_mobile: '',
        fmb_incharge_name: '',
        fmb_incharge_mobile: ''
      });
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to add mohalla: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateMohalla = async (id, data) => {
    try {
      await api.put(`/mohallas/${id}`, data);
      toast.success('Mohalla updated successfully');
      setEditingItem(null);
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to update mohalla');
    }
  };

  const handleDeleteMohalla = async (id) => {
    try {
      await api.delete(`/mohallas/${id}`);
      toast.success('Mohalla deleted successfully');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to delete mohalla: ' + (error.response?.data?.detail || error.message));
    }
  };

  // === SECTOR CRUD ===
  const handleAddSector = async () => {
    if (!newSector.name.trim() || !newSector.mohalla) {
      toast.error('Sector name and Mohalla are required');
      return;
    }
    try {
      await api.post('/sectors', newSector);
      toast.success('Sector added successfully');
      setShowAddSector(false);
      setNewSector({ 
        name: '', 
        mohalla: '', 
        sub_sector_name: '',
        musaid_name: '', 
        musaid_mobile: '', 
        musaida_name: '', 
        musaida_mobile: ''
      });
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to add sector: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateSector = async (id, data) => {
    try {
      await api.put(`/sectors/${id}`, data);
      toast.success('Sector updated successfully');
      setEditingItem(null);
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to update sector');
    }
  };

  const handleDeleteSector = async (id) => {
    try {
      await api.delete(`/sectors/${id}`);
      toast.success('Sector deleted successfully');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchData();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to delete sector: ' + (error.response?.data?.detail || error.message));
    }
  };

  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setShowDeleteConfirm(true);
  };

  // === DOWNLOAD ===
  const handleDownload = async () => {
    try {
      setExporting(true);
      
      let url;
      let fileName;
      
      if (listType === 'shaadi') {
        // Shaadi List Export - now uses scope parameter like standard list
        url = `/export/shaadi-list?scope=${downloadScope}&format=${downloadFormat}`;
        if (downloadScope === 'mohalla' && downloadMohalla) {
          url += `&mohalla=${encodeURIComponent(downloadMohalla)}`;
        }
        if (downloadScope === 'sector' && downloadSector) {
          url += `&mohalla=${encodeURIComponent(downloadMohalla)}`;
          url += `&sector=${encodeURIComponent(downloadSector)}`;
        }
        if (customHeading) {
          url += `&custom_heading=${encodeURIComponent(customHeading)}`;
        } else {
          url += `&custom_heading=SHAADI%20LIST`;
        }
        
        const scopeName = downloadScope === 'sector' && downloadSector 
          ? downloadSector.replace(/ /g, '_') 
          : downloadScope === 'mohalla' && downloadMohalla 
            ? downloadMohalla.replace(/ /g, '_')
            : 'all';
        fileName = `shaadi_list_${scopeName}.${downloadFormat === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else {
        // Standard List Export
        url = `/export/sector-list-pdf?scope=${downloadScope}&format=${downloadFormat}`;
        if (downloadScope !== 'all' && downloadScope !== 'alphabetical') {
          url += `&mohalla=${encodeURIComponent(downloadMohalla)}`;
        }
        if (downloadScope === 'sector' && downloadSector) {
          url += `&sector=${encodeURIComponent(downloadSector)}`;
        }
        if (customHeading) {
          url += `&custom_heading=${encodeURIComponent(customHeading)}`;
        }
        if (sectorSubheading) {
          url += `&sector_subheading=${encodeURIComponent(sectorSubheading)}`;
        }
        if (isFmb) {
          url += `&is_fmb=true`;
        }
        // Include member count (Super Admin only)
        if (includeMemberCount && isSuperAdmin) {
          url += `&include_member_count=true`;
        }
        
        const scopeName = downloadScope === 'all' ? 'all_mohallas' : 
                          downloadScope === 'alphabetical' ? 'all_mohallas_alphabetical' : 
                          downloadMohalla.replace(/ /g, '_');
        fileName = `sector_list_${scopeName}.${downloadFormat === 'pdf' ? 'pdf' : 'xlsx'}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      
      const mimeType = downloadFormat === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      toast.success(`${downloadFormat.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const canDownload = () => {
    if (listType === 'shaadi') {
      // Shaadi list can be downloaded for all, mohalla, or sector
      if (downloadScope === 'all') return true;
      if (downloadScope === 'mohalla') return downloadMohalla !== '';
      if (downloadScope === 'sector') return downloadMohalla !== '' && downloadSector !== '';
      return true;
    }
    // Standard list
    if (downloadScope === 'all') return true;
    if (downloadScope === 'alphabetical') return true;
    if (downloadScope === 'mohalla') return downloadMohalla !== '';
    if (downloadScope === 'sector') return downloadMohalla !== '' && downloadSector !== '';
    return false;
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 mb-2">Access Denied</h2>
            <p className="text-red-600">Only Admin users can manage Mohalla & Sector master data.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-navy">Mohalla & Sector</h1>
          <p className="text-slate-500 text-xs sm:text-sm">Master data management</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-max min-w-full sm:grid sm:w-full sm:grid-cols-4">
            <TabsTrigger value="mohallas" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Mohallas</span> ({mohallas.length})
            </TabsTrigger>
            <TabsTrigger value="sectors" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Sectors</span> ({sectors.length})
            </TabsTrigger>
            <TabsTrigger value="download" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Download</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <History className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Logs</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Mohallas Tab */}
        <TabsContent value="mohallas">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-6">
              <CardTitle className="text-base sm:text-lg">Mohalla List</CardTitle>
              <Button onClick={() => setShowAddMohalla(true)} className="bg-navy hover:bg-navy/90 text-xs sm:text-sm" size="sm">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Add Mohalla
              </Button>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[700px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Name</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Area</TableHead>
                      <TableHead className="hidden lg:table-cell whitespace-nowrap text-xs sm:text-sm">Address</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Masool</TableHead>
                      <TableHead className="hidden md:table-cell whitespace-nowrap text-xs sm:text-sm">Masoola</TableHead>
                      <TableHead className="hidden lg:table-cell whitespace-nowrap text-xs sm:text-sm">FMB Incharge</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Sectors</TableHead>
                      <TableHead className="text-right whitespace-nowrap text-xs sm:text-sm">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : mohallas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">No mohallas found</TableCell>
                      </TableRow>
                    ) : (
                      mohallas.map((m) => (
                        <TableRow key={m.id || m.name}>
                          <TableCell className="font-medium text-xs sm:text-sm">{m.name}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-600">{m.area || '-'}</TableCell>
                          <TableCell className="hidden lg:table-cell text-xs sm:text-sm">{m.address || '-'}</TableCell>
                          <TableCell>
                            {m.masool_name ? (
                              <div className="text-xs sm:text-sm">
                                <p className="truncate max-w-[100px] sm:max-w-none">{m.masool_name}</p>
                                <p className="text-slate-500 text-[10px] sm:text-xs">{m.masool_mobile}</p>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {m.masoola_name ? (
                              <div className="text-xs sm:text-sm">
                                <p className="truncate max-w-[100px]">{m.masoola_name}</p>
                                <p className="text-slate-500 text-[10px] sm:text-xs">{m.masoola_mobile}</p>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {m.fmb_incharge_name ? (
                              <div className="text-xs sm:text-sm">
                                <p className="truncate max-w-[100px]">{m.fmb_incharge_name}</p>
                                <p className="text-slate-500 text-[10px] sm:text-xs">{m.fmb_incharge_mobile}</p>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              {sectors.filter(s => s.mohalla === m.name).length}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingItem({ type: 'mohalla', ...m })}
                              >
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                onClick={() => confirmDelete('mohalla', m)}
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sectors Tab */}
        <TabsContent value="sectors">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-6">
              <CardTitle className="text-base sm:text-lg">Sector List</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={sectorAreaFilter || 'all'} onValueChange={(v) => setSectorAreaFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-36 text-xs sm:text-sm" data-testid="sector-area-filter">
                    <SelectValue placeholder="Filter by Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Areas</SelectItem>
                    {areaOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={() => setShowAddSector(true)} className="bg-navy hover:bg-navy/90 text-xs sm:text-sm" size="sm">
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Add Sector
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Area</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Sector</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">Mohalla</TableHead>
                      <TableHead className="hidden md:table-cell whitespace-nowrap text-xs sm:text-sm">Musaid</TableHead>
                      <TableHead className="hidden lg:table-cell whitespace-nowrap text-xs sm:text-sm">Musaida</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">HOFs</TableHead>
                      <TableHead className="text-right whitespace-nowrap text-xs sm:text-sm">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : displaySectors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">No sectors found</TableCell>
                      </TableRow>
                    ) : (
                      displaySectors.map((s) => (
                        <TableRow key={s.id || `${s.mohalla}_${s.name}`}>
                          <TableCell className="text-xs sm:text-sm text-slate-600">{s.area || '-'}</TableCell>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 shrink-0" />
                              <span className="truncate max-w-[80px] sm:max-w-none">{s.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm truncate max-w-[60px] sm:max-w-none">{s.mohalla}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {s.musaid_name ? (
                              <div className="text-xs sm:text-sm">
                                <p className="truncate max-w-[120px]">{s.musaid_name}</p>
                                <p className="text-slate-500 text-[10px] sm:text-xs">{s.musaid_mobile}</p>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {s.musaida_name ? (
                              <div className="text-xs sm:text-sm">
                                <p className="truncate max-w-[120px]">{s.musaida_name}</p>
                                <p className="text-slate-500 text-[10px] sm:text-xs">{s.musaida_mobile}</p>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              {s.hof_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingItem({ type: 'sector', ...s })}
                              >
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                onClick={() => confirmDelete('sector', s)}
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Download Tab */}
        <TabsContent value="download">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Download Sector List
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Format Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">PDF Format Specifications:</h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li><b>One sector per page</b> with automatic page breaks</li>
                  <li>Landscape A4 orientation, 0.5 inch margins</li>
                  <li>Font: Times, Size 14</li>
                  <li>Headers: Org Name → Custom Title → Mohalla → Sector No</li>
                  <li><b>Regular List:</b> Shows Masool, Masoola, Musaid, Musaida details</li>
                  <li><b>FMB List:</b> Shows "Distribution Incharge" only</li>
                  <li>Columns: Sr No, ITS Number, HOF Name, Mobile</li>
                </ul>
              </div>

              {/* Download Criteria */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left - List Type & Scope Selection */}
                <div className="space-y-4">
                  {/* List Type Selection */}
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    List Type
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div 
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        listType === 'standard' ? 'border-navy bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setListType('standard')}
                      data-testid="list-type-standard"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className={`w-5 h-5 ${listType === 'standard' ? 'text-navy' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium text-sm">Standard List</p>
                          <p className="text-xs text-slate-500">HOF with address details</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        listType === 'shaadi' ? 'border-pink-500 bg-pink-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setListType('shaadi')}
                      data-testid="list-type-shaadi"
                    >
                      <div className="flex items-center gap-2">
                        <Heart className={`w-5 h-5 ${listType === 'shaadi' ? 'text-pink-500' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium text-sm">Shaadi List</p>
                          <p className="text-xs text-slate-500">HOF + Head Lady only</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2 mt-4">
                    <Filter className="w-4 h-4" />
                    Download Scope
                  </h4>
                  
                  <div className="space-y-3">
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        downloadScope === 'all' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDownloadScope('all')}
                    >
                      <div className="flex items-center gap-3">
                        <Globe className={`w-5 h-5 ${downloadScope === 'all' ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium">All Mohallas</p>
                          <p className="text-xs text-slate-500">Complete export - all sectors across all mohallas</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        downloadScope === 'alphabetical' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDownloadScope('alphabetical')}
                    >
                      <div className="flex items-center gap-3">
                        <SortAsc className={`w-5 h-5 ${downloadScope === 'alphabetical' ? 'text-amber-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium">Alphabetical List</p>
                          <p className="text-xs text-slate-500">All mohallas sorted A-Z with their sectors</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        downloadScope === 'mohalla' ? 'border-navy bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDownloadScope('mohalla')}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${downloadScope === 'mohalla' ? 'text-navy' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium">Mohalla-wise</p>
                          <p className="text-xs text-slate-500">All sectors under one mohalla</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        downloadScope === 'sector' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDownloadScope('sector')}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className={`w-5 h-5 ${downloadScope === 'sector' ? 'text-purple-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium">Sector-wise</p>
                          <p className="text-xs text-slate-500">Single sector only</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mohalla Selection */}
                  {(downloadScope === 'mohalla' || downloadScope === 'sector') && (
                    <div className="space-y-2">
                      <Label>Select Mohalla *</Label>
                      <Select value={downloadMohalla} onValueChange={setDownloadMohalla}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose Mohalla..." />
                        </SelectTrigger>
                        <SelectContent>
                          {mohallas.map((m) => (
                            <SelectItem key={m.id || m.name} value={m.name}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Sector Selection */}
                  {downloadScope === 'sector' && downloadMohalla && (
                    <div className="space-y-2">
                      <Label>Select Sector *</Label>
                      <Select value={downloadSector} onValueChange={setDownloadSector}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose Sector..." />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredSectors.map((s) => (
                            <SelectItem key={s.id || s.name} value={s.name}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Right - Format & Options */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800">Export Options</h4>
                  
                  {/* FMB Toggle */}
                  <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div>
                      <p className="font-medium text-orange-800">FMB Distribution List</p>
                      <p className="text-xs text-orange-600">Shows "Distribution Incharge" in header</p>
                    </div>
                    <Checkbox 
                      checked={isFmb} 
                      onCheckedChange={setIsFmb}
                      className="border-orange-400"
                    />
                  </div>
                  
                  {/* Include Member Count - Super Admin Only */}
                  {isSuperAdmin && (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div>
                        <p className="font-medium text-emerald-800">Include Member Count</p>
                        <p className="text-xs text-emerald-600">Shows family member count in export (Super Admin only)</p>
                      </div>
                      <Checkbox 
                        checked={includeMemberCount} 
                        onCheckedChange={setIncludeMemberCount}
                        className="border-emerald-400"
                      />
                    </div>
                  )}
                  
                  {/* Custom Heading */}
                  <div className="space-y-2">
                    <Label>Custom Heading (Optional)</Label>
                    <Input 
                      value={customHeading}
                      onChange={(e) => setCustomHeading(e.target.value)}
                      placeholder={isFmb ? "FMB DISTRIBUTION LIST" : "e.g., SECTOR LIST"}
                    />
                    <p className="text-xs text-slate-500">Appears below organization name</p>
                  </div>
                  
                  {/* Sector Sub-heading */}
                  <div className="space-y-2">
                    <Label>Sector Sub-heading (Optional)</Label>
                    <Input 
                      value={sectorSubheading}
                      onChange={(e) => setSectorSubheading(e.target.value)}
                      placeholder="e.g., NORTH ZONE, AREA A"
                    />
                    <p className="text-xs text-slate-500">Appears after "SECTOR NO X" (e.g., SECTOR NO 1 - NORTH ZONE)</p>
                  </div>
                  
                  {/* Format Selection */}
                  <div className="space-y-2">
                    <Label>Export Format</Label>
                    <div className={`grid gap-3 ${canExportExcel ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <Button 
                        variant={downloadFormat === 'pdf' ? 'default' : 'outline'}
                        className={`h-16 flex flex-col items-center justify-center gap-1 ${downloadFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        onClick={() => setDownloadFormat('pdf')}
                      >
                        <FileText className="w-5 h-5" />
                        <span>PDF</span>
                      </Button>
                      {/* Excel option - Super Admin ONLY */}
                      {canExportExcel && (
                        <Button 
                          variant={downloadFormat === 'excel' ? 'default' : 'outline'}
                          className={`h-16 flex flex-col items-center justify-center gap-1 ${downloadFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          onClick={() => setDownloadFormat('excel')}
                        >
                          <FileSpreadsheet className="w-5 h-5" />
                          <span>Excel</span>
                        </Button>
                      )}
                    </div>
                    {!canExportExcel && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        📋 Excel export is restricted to Super Admin only
                      </p>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <p className="font-medium text-sm mb-2">Download Summary:</p>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><b>List Type:</b> {listType === 'shaadi' ? '💒 Shaadi List (HOF + Head Lady)' : '📋 Standard List'}</p>
                      <p><b>Scope:</b> {
                        downloadScope === 'all' ? 'All Mohallas' : 
                        downloadScope === 'alphabetical' ? 'All Mohallas (A-Z Sorted)' :
                        downloadScope === 'mohalla' ? `${downloadMohalla || 'Select Mohalla'}` : 
                        `${downloadMohalla || 'Select Mohalla'} → ${downloadSector || 'Select Sector'}`
                      }</p>
                      {listType !== 'shaadi' && <p><b>Type:</b> {isFmb ? 'FMB Distribution List' : 'Sector List'}</p>}
                      <p><b>Format:</b> {downloadFormat.toUpperCase()}</p>
                      {includeMemberCount && isSuperAdmin && listType !== 'shaadi' && <p><b>Member Count:</b> Included</p>}
                      {customHeading && listType !== 'shaadi' && <p><b>Heading:</b> {customHeading}</p>}
                      {sectorSubheading && listType !== 'shaadi' && <p><b>Sector Sub:</b> {sectorSubheading}</p>}
                    </div>
                  </div>

                  {/* Download Button */}
                  <Button 
                    onClick={handleDownload} 
                    disabled={!canDownload() || exporting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                  >
                    {exporting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Download {downloadFormat.toUpperCase()}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Change History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">No audit logs found</TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.slice(0, 50).map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            log.change_type === 'CREATE' ? 'bg-green-100 text-green-700' :
                            log.change_type === 'DELETE' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }>
                            {log.change_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{log.entity_type}</span>
                          {log.entity_id && <span className="text-slate-500 ml-1">({log.entity_id})</span>}
                        </TableCell>
                        <TableCell>{log.changed_by}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                          {log.remarks || log.new_value || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Mohalla Dialog */}
      <Dialog open={showAddMohalla} onOpenChange={setShowAddMohalla}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Mohalla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Mohalla Name *</Label>
              <Input 
                value={newMohalla.name}
                onChange={(e) => setNewMohalla({...newMohalla, name: e.target.value})}
                placeholder="e.g., HASANI MOHALLA"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input 
                value={newMohalla.address}
                onChange={(e) => setNewMohalla({...newMohalla, address: e.target.value})}
                placeholder="e.g., NAGPUR"
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 text-sm mb-2">Mohalla Leadership (Masool/Masoola)</h4>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Masool (Search by name)</Label>
                  <MemberSearchInput
                    value={newMohalla.masool_name}
                    onChange={(val, member) => {
                      setNewMohalla({
                        ...newMohalla, 
                        masool_name: member ? member.full_name : val,
                        masool_mobile: member?.mobile || newMohalla.masool_mobile
                      });
                    }}
                    members={allMembers}
                    placeholder="Search member..."
                    showMobile={false}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Masoola (Search by name)</Label>
                  <MemberSearchInput
                    value={newMohalla.masoola_name}
                    onChange={(val, member) => {
                      setNewMohalla({
                        ...newMohalla, 
                        masoola_name: member ? member.full_name : val,
                        masoola_mobile: member?.mobile || newMohalla.masoola_mobile
                      });
                    }}
                    members={allMembers}
                    placeholder="Search member..."
                    showMobile={false}
                  />
                </div>
              </div>
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="font-medium text-orange-800 text-sm mb-2">FMB Distribution Incharge</h4>
              <div className="space-y-1">
                <Label className="text-xs">FMB Incharge (Search by name or enter full name)</Label>
                <MemberSearchInput
                  value={newMohalla.fmb_incharge_name}
                  onChange={(val, member) => {
                    setNewMohalla({
                      ...newMohalla, 
                      fmb_incharge_name: member ? member.full_name : val,
                      fmb_incharge_mobile: member?.mobile || newMohalla.fmb_incharge_mobile
                    });
                  }}
                  members={allMembers}
                  placeholder="Search or enter full name..."
                  showMobile={false}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleAddMohalla} className="bg-navy hover:bg-navy/90">
              Add Mohalla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sector Dialog */}
      <Dialog open={showAddSector} onOpenChange={setShowAddSector}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Sector</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link to Mohalla *</Label>
                <Select value={newSector.mohalla} onValueChange={(v) => setNewSector({...newSector, mohalla: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Mohalla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {mohallas.map((m) => (
                      <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sector Name/Number *</Label>
                <Input 
                  value={newSector.name}
                  onChange={(e) => setNewSector({...newSector, name: e.target.value})}
                  placeholder="e.g., SECTOR NO 21"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sub-Sector Name (Optional)</Label>
              <Input 
                value={newSector.sub_sector_name}
                onChange={(e) => setNewSector({...newSector, sub_sector_name: e.target.value})}
                placeholder="e.g., NORTH ZONE, AREA A"
              />
              <p className="text-xs text-slate-500">Appears in PDF as "SECTOR NO X - SUB NAME"</p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 text-sm mb-2">Sector Leadership (Musaid/Musaida)</h4>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Musaid (Search by name, e.g., "murtaza raipur")</Label>
                  <MemberSearchInput
                    value={newSector.musaid_name}
                    onChange={(val, member) => {
                      setNewSector({
                        ...newSector, 
                        musaid_name: member ? member.full_name : val,
                        musaid_mobile: member?.mobile || newSector.musaid_mobile
                      });
                    }}
                    members={allMembers}
                    placeholder="Search member..."
                    showMobile={false}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Musaida (Search by name)</Label>
                  <MemberSearchInput
                    value={newSector.musaida_name}
                    onChange={(val, member) => {
                      setNewSector({
                        ...newSector, 
                        musaida_name: member ? member.full_name : val,
                        musaida_mobile: member?.mobile || newSector.musaida_mobile
                      });
                    }}
                    members={allMembers}
                    placeholder="Search member..."
                    showMobile={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleAddSector} className="bg-navy hover:bg-navy/90">
              Add Sector
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{deleteTarget?.item?.name}</strong>?
            </p>
            {deleteTarget?.type === 'mohalla' && (
              <p className="text-red-600 text-sm mt-2">
                Warning: This will also affect all sectors linked to this mohalla.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget?.type === 'mohalla' 
                ? handleDeleteMohalla(deleteTarget.item.id)
                : handleDeleteSector(deleteTarget.item.id)
              }
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {editingItem && (
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit {editingItem.type === 'mohalla' ? 'Mohalla' : 'Sector'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {editingItem.type === 'mohalla' ? (
                <>
                  <div className="space-y-2">
                    <Label>Mohalla Name *</Label>
                    <Input 
                      value={editingItem.name}
                      onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input 
                      value={editingItem.address || ''}
                      onChange={(e) => setEditingItem({...editingItem, address: e.target.value})}
                    />
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 text-sm mb-2">Mohalla Leadership (Masool/Masoola)</h4>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Masool (Search by name)</Label>
                        <MemberSearchInput
                          value={editingItem.masool_name || ''}
                          onChange={(val, member) => {
                            setEditingItem({
                              ...editingItem, 
                              masool_name: member ? member.full_name : val,
                              masool_mobile: member?.mobile || editingItem.masool_mobile,
                              masool_its: member?.its_id || editingItem.masool_its
                            });
                          }}
                          members={allMembers}
                          placeholder="Search member..."
                          showMobile={false}
                        />
                        <Input 
                          placeholder="ITS ID (8 digits) - for auto-login"
                          value={editingItem.masool_its || ''}
                          onChange={(e) => setEditingItem({...editingItem, masool_its: e.target.value})}
                          maxLength={8}
                          className="mt-1 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Masoola (Search by name)</Label>
                        <MemberSearchInput
                          value={editingItem.masoola_name || ''}
                          onChange={(val, member) => {
                            setEditingItem({
                              ...editingItem, 
                              masoola_name: member ? member.full_name : val,
                              masoola_mobile: member?.mobile || editingItem.masoola_mobile,
                              masoola_its: member?.its_id || editingItem.masoola_its
                            });
                          }}
                          members={allMembers}
                          placeholder="Search member..."
                          showMobile={false}
                        />
                        <Input 
                          placeholder="ITS ID (8 digits) - for auto-login"
                          value={editingItem.masoola_its || ''}
                          onChange={(e) => setEditingItem({...editingItem, masoola_its: e.target.value})}
                          maxLength={8}
                          className="mt-1 text-xs"
                        />
                      </div>
                      <p className="text-[10px] text-blue-600 mt-2">* Adding ITS ID will auto-create login (username=ITS, password=ITS)</p>
                    </div>
                  </div>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="font-medium text-orange-800 text-sm mb-2">FMB Distribution Incharge</h4>
                    <div className="space-y-1">
                      <Label className="text-xs">FMB Incharge (Search by name or enter full name)</Label>
                      <MemberSearchInput
                        value={editingItem.fmb_incharge_name || ''}
                        onChange={(val, member) => {
                          setEditingItem({
                            ...editingItem, 
                            fmb_incharge_name: member ? member.full_name : val,
                            fmb_incharge_mobile: member?.mobile || editingItem.fmb_incharge_mobile
                          });
                        }}
                        members={allMembers}
                        placeholder="Search or enter full name..."
                        showMobile={false}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mohalla</Label>
                      <Select 
                        value={editingItem.mohalla} 
                        onValueChange={(v) => setEditingItem({...editingItem, mohalla: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {mohallas.map((m) => (
                            <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Sector Name *</Label>
                      <Input 
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Sub-Sector Name (Optional)</Label>
                    <Input 
                      value={editingItem.sub_sector_name || ''}
                      onChange={(e) => setEditingItem({...editingItem, sub_sector_name: e.target.value})}
                      placeholder="e.g., NORTH ZONE, AREA A"
                    />
                    <p className="text-xs text-slate-500">Appears in PDF as "SECTOR NO X - SUB NAME"</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 text-sm mb-2">Sector Leadership (Musaid/Musaida)</h4>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Musaid (Search by name)</Label>
                        <MemberSearchInput
                          value={editingItem.musaid_name || ''}
                          onChange={(val, member) => {
                            setEditingItem({
                              ...editingItem, 
                              musaid_name: member ? member.full_name : val,
                              musaid_mobile: member?.mobile || editingItem.musaid_mobile,
                              musaid_its: member?.its_id || editingItem.musaid_its
                            });
                          }}
                          members={allMembers}
                          placeholder="Search member..."
                          showMobile={false}
                        />
                        <Input 
                          placeholder="ITS ID (8 digits) - for auto-login"
                          value={editingItem.musaid_its || ''}
                          onChange={(e) => setEditingItem({...editingItem, musaid_its: e.target.value})}
                          maxLength={8}
                          className="mt-1 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Musaida (Search by name)</Label>
                        <MemberSearchInput
                          value={editingItem.musaida_name || ''}
                          onChange={(val, member) => {
                            setEditingItem({
                              ...editingItem, 
                              musaida_name: member ? member.full_name : val,
                              musaida_mobile: member?.mobile || editingItem.musaida_mobile,
                              musaida_its: member?.its_id || editingItem.musaida_its
                            });
                          }}
                          members={allMembers}
                          placeholder="Search member..."
                          showMobile={false}
                        />
                        <Input 
                          placeholder="ITS ID (8 digits) - for auto-login"
                          value={editingItem.musaida_its || ''}
                          onChange={(e) => setEditingItem({...editingItem, musaida_its: e.target.value})}
                          maxLength={8}
                          className="mt-1 text-xs"
                        />
                      </div>
                      <p className="text-[10px] text-blue-600 mt-2">* Adding ITS ID will auto-create login (username=ITS, password=ITS)</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
              <Button 
                className="bg-navy hover:bg-navy/90"
                onClick={() => editingItem.type === 'mohalla'
                  ? handleUpdateMohalla(editingItem.id, editingItem)
                  : handleUpdateSector(editingItem.id, editingItem)
                }
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
